/**
 * Certificate number formatting (spec §7.6.2b).
 *
 * This lives in shared — not in the API — for one specific reason: the format editor
 * shows the administrator a live preview of the next number, and that preview must be
 * produced by the *same code* that later prints the number on a legal trade document.
 * Two implementations would eventually disagree, and the disagreement would surface as
 * a wrong number on an issued certificate.
 *
 * What is emphatically NOT here: drawing the next sequence value. That is an atomic
 * database operation (D-005) and cannot be done anywhere but inside the approval
 * transaction.
 */
import { z } from 'zod';
import { sequenceResetCadenceSchema, sequenceScopeSchema, } from './enums.js';
export const certificateNumberFormatSchema = z.object({
    /** Free text or a token: {YYYY}, {YY}, {MM}. Resolved at issue time. */
    prefix: z.string().max(30).default(''),
    separator: z.string().max(5).default(''),
    sequenceWidth: z.number().int().min(1).max(12).default(6),
    suffix: z.string().max(30).default(''),
    resetCadence: sequenceResetCadenceSchema.default('NEVER'),
    /** One counter for the whole chamber, or one per document type. See D-011. */
    sequenceScope: sequenceScopeSchema.default('CHAMBER'),
    /** `{n}` is the amendment ordinal, e.g. `-A{n}` → `-A1`. */
    amendmentSuffixPattern: z.string().max(20).default('-A{n}'),
});
/**
 * The period a sequence counter is currently tracking. Comparing the stored key against
 * the key for the issue date is what makes a reset happen — no scheduled job, no
 * midnight cron, nothing to miss if the server was down at the rollover moment.
 */
export function periodKeyFor(cadence, at) {
    switch (cadence) {
        case 'NEVER':
            return 'ALL';
        case 'YEARLY':
            return String(at.getUTCFullYear());
        case 'MONTHLY':
            return `${at.getUTCFullYear()}-${String(at.getUTCMonth() + 1).padStart(2, '0')}`;
    }
}
export function resolveTokens(template, at) {
    const year = at.getUTCFullYear();
    return template
        .replace(/\{YYYY\}/g, String(year))
        .replace(/\{YY\}/g, String(year).slice(-2))
        .replace(/\{MM\}/g, String(at.getUTCMonth() + 1).padStart(2, '0'));
}
/** Assembles a number from an already-drawn sequence value. Pure. */
export function formatCertificateNumber(format, sequenceValue, issuedAt) {
    const prefix = resolveTokens(format.prefix, issuedAt);
    const suffix = resolveTokens(format.suffix, issuedAt);
    const body = String(sequenceValue).padStart(format.sequenceWidth, '0');
    const sep = format.separator;
    return [prefix, body, suffix].filter((part) => part.length > 0).join(sep);
}
/**
 * An amendment derives from the original number and consumes NO sequence value
 * (spec §9.5). The ordinal counts existing amendments of that original, so the second
 * amendment of `BCQ-000123` is `BCQ-000123-A2` — which stays matchable at the border
 * against the original it replaces.
 */
export function formatAmendmentNumber(originalCertificateNo, amendmentSuffixPattern, amendmentOrdinal) {
    return originalCertificateNo + amendmentSuffixPattern.replace(/\{n\}/g, String(amendmentOrdinal));
}
/** Drives the live preview in the format editor. */
export function previewCertificateNumber(format, nextSequenceValue, at = new Date()) {
    const next = formatCertificateNumber(format, nextSequenceValue, at);
    return {
        next,
        afterThat: formatCertificateNumber(format, nextSequenceValue + 1, at),
        amendmentExample: formatAmendmentNumber(next, format.amendmentSuffixPattern, 1),
    };
}
/**
 * A short human-transcribable code printed beside the certificate number, required to
 * verify publicly. Without it, the verification portal would let anyone walk the
 * certificate number space and enumerate every certificate a chamber has ever issued.
 *
 * Crockford base32, minus I/L/O/U — no character pair a person can confuse when reading
 * it off a printed page, and no accidental words.
 */
const CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export function encodeVerificationCode(bytes, length = 6) {
    let out = '';
    for (let i = 0; i < length; i += 1) {
        out += CODE_ALPHABET[(bytes[i] ?? 0) % CODE_ALPHABET.length];
    }
    return out;
}
export function normaliseVerificationCode(input) {
    return input
        .toUpperCase()
        .replace(/[\s-]/g, '')
        .replace(/[ILO]/g, (c) => ({ I: '1', L: '1', O: '0' })[c] ?? c)
        .replace(/U/g, 'V');
}
// ── Amendment limits (D-029) ─────────────────────────────────────────────────
/** 0 means "no amendments: apply for a new certificate". 20 is a sanity ceiling, not a policy. */
export const amendmentLimitSchema = z.number().int().min(0).max(20);
/**
 * How many times an exporter may amend one certificate. A chamber-wide default, and an
 * optional override per certificate type (null: use the chamber's).
 */
export const amendmentLimitsSchema = z.object({
    chamberDefault: amendmentLimitSchema,
    overrides: z
        .array(z.object({ documentTypeId: z.string().min(1), limit: amendmentLimitSchema.nullable() }))
        .max(100),
});
//# sourceMappingURL=certificate-number.js.map